import { useState, useEffect, useRef } from 'react';
import {
  TextInput,
  ComboBox,
  Select,
  CountrySelect,
  PhoneNumberInput,
  NumberInput,
  Autocomplete,
  Option,
  menuItemColor,
  DatePicker,
} from 'components/inputs';
import { Chip, Text } from 'components';
import type { InputProps, Fields, Phone, ObjectValue } from 'features/form/types';
import type { CountryCode, Country } from 'components/inputs/CountrySelect';
import { t } from '@lingui/macro';
import styled from '@emotion/styled';
import { colors } from 'styles/theme/colors';
import type { ComboBoxOption } from 'components/inputs/types';
import { useTheme } from '@emotion/react';
import { Box } from '@mui/material';
import { useLingui } from '@lingui/react';
import { parseDate, formatDate } from '../../misc/table/components/filter/transforms';

export const InputField = ({
  field,
  w = 100,
  onSelect,
  hideLabel = false,
}: {
  field: InputProps | Fields[];
  w?: number;
  onSelect?: (val: unknown) => void;
  hideLabel?: boolean;
}) => {
  if (Array.isArray(field)) {
    return null;
  }
  return (
    <InputContainer w={w}>
      <Input {...field} onSelect={onSelect} hideLabel={hideLabel} />
    </InputContainer>
  );
};

const InputContainer = styled.div<{ w: number }>`
  display: flex;
  width: ${p => (p.w < 100 ? `calc(${p.w}% - 8px)` : '100%')};
`;

export const Input = ({
  onChange,
  value,
  label,
  placeholder,
  required,
  type,
  api,
  id,
  options,
  objectValue,
  onSelect,
  autoFocus,
  disabled,
  error: fieldError,
  touched: fieldTouched,
  hideLabel = false,
  autoComplete,
  icon,
  endIcon,
  inputType,
  helperText,
  groupBy,
  freeSolo = true,
  shouldDisableDate,
}: InputProps & { onSelect?: (val: unknown) => void; hideLabel?: boolean }) => {
  const [inputValue, setInputValue] = useState<string>('');
  const countryInputRef = useRef<string>('');
  const [phone, setPhone] = useState<Phone | undefined>();
  const [multipleValues, setMultipleValues] = useState<Option[]>([]);

  const { _ } = useLingui();
  const theme = useTheme();
  const debounceRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (
      value != null &&
      typeof value === 'object' &&
      (type === 'comboavatar' || type === 'combo')
    ) {
      setInputValue(value as unknown as string);
    }
    if (value != null && typeof value === 'string') {
      if (type === 'date') {
        setInputValue(parseDate(value) as unknown as string);
      } else {
        setInputValue(value);
      }
      if (type === 'country' && value) {
        countryInputRef.current = value;
      }
    }
    if (type === 'phone' && objectValue) {
      setPhone(objectValue as Phone);
    }
    if (type === 'multiple' && Array.isArray(objectValue)) {
      setMultipleValues(objectValue as Option[]);
    }
  }, [value, objectValue, type]);

  const debounceOnChange = (a: string, b?: ObjectValue) => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!onChange) return;
    debounceRef.current = setTimeout(() => {
      onChange(a, b);
    }, 400);
  };

  const handleTextChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { value: inputVal } = e.target;
    setInputValue(inputVal);
    debounceOnChange(inputVal);
  };

  const handleTextBlur = (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (onChange) {
      onChange(e.target.value); // Ensure latest value is sent on blur too
    }
  };

  const handledateChange = (date: string) => {
    setInputValue(date);
    debounceOnChange(date);
  };

  const handleDateBlur = (date: string) => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (onChange) {
      onChange(date); // Ensure latest value is sent on blur too
    }
  };

  const handleComboBoxChange = (
    e: React.SyntheticEvent,
    v: string | ComboBoxOption | ComboBoxOption[] | null,
    r: string
  ) => {
    setInputValue(v as string);
    if (r === 'selectOption' && onSelect) {
      onSelect(v);
    }
    debounceOnChange(v as string);
  };

  const handleMultipleChange = (e: React.SyntheticEvent, v: Option[]) => {
    setMultipleValues(v);
    debounceOnChange('', v);
  };

  const handleCountryChange = (val: CountryCode) => {
    setInputValue(val);
    countryInputRef.current = val;
    if (onChange) {
      onChange(val);
    }
  };

  const handlePhoneNumberChange = (val: (Country & { phoneNumber?: string }) | undefined) => {
    if (!val) {
      setPhone(undefined);
      debounceOnChange('', undefined);
      return;
    }
    const newVal = { ...phone, ...val };
    setPhone(newVal);
    if (onChange && newVal.country && newVal.country !== phone?.country) {
      onChange('', newVal);
      return;
    }
    debounceOnChange('', newVal);
  };
  const hasError = fieldTouched && Boolean(fieldError);
  let helper = hasError ? _(fieldError || '') : '';
  if (!hasError && helperText) {
    helper = helperText;
  }

  const labelText = hideLabel ? '\u00A0' : _(label || '');

  const placeholderText = placeholder ? _(placeholder) : '';

  if (type === 'text') {
    const inputProps: { startAdornment?: React.ReactNode; endAdornment?: React.ReactNode } = {};
    if (icon) {
      inputProps.startAdornment = icon;
    }
    if (endIcon) {
      inputProps.endAdornment = endIcon;
    }
    return (
      <TextInput
        value={inputValue}
        onChange={handleTextChange}
        onBlur={handleTextBlur}
        error={hasError}
        disabled={disabled}
        variant="outlined"
        placeholder={placeholderText}
        label={labelText}
        helperText={helper}
        fullWidth
        required={required}
        labelWeight="semiBold"
        labelColor={colors.grey[400]}
        autoFocus={autoFocus}
        autoComplete={autoComplete}
        InputProps={icon || endIcon ? inputProps : undefined}
        type={inputType ? inputType : undefined}
      />
    );
  }

  if (['combo', 'comboavatar'].indexOf(type) > -1) {
    return (
      <ComboBox
        id={`combo-${id}`}
        labelSeparate
        labelWeight="semiBold"
        fullWidth
        inputVariant="outlined"
        labelColor={colors.grey[400]}
        label={labelText}
        placeholder={placeholderText}
        required={required}
        freeSolo={freeSolo}
        {...(freeSolo ? { onInputChange: handleComboBoxChange } : {})}
        hasAvatar={type === 'comboavatar'}
        value={inputValue}
        onChange={(e, v, r) => handleComboBoxChange(e, v, r)}
        onBlur={() => {
          if (debounceRef.current) clearTimeout(debounceRef.current);
          onChange && onChange(inputValue);
        }}
        groupBy={groupBy}
        api={api}
        error={hasError}
        helperText={helper}
        autoFocus={autoFocus}
        options={options}
      />
    );
  }

  if (type === 'multiple') {
    return (
      <Autocomplete
        labelSeparate
        labelWeight="semiBold"
        fullWidth
        variant="outlined"
        error={hasError}
        helperText={helper}
        labelColor={colors.grey[400]}
        label={
          <>
            {`${labelText} `}
            <span className="MuiFormLabel-asterisk MuiInputLabel-asterisk">*</span>
          </>
        }
        isOptionEqualToValue={(option, selectedValue) => {
          return Boolean(String(option?.id) === String(selectedValue?.id));
        }}
        filterSelectedOptions
        placeholder={placeholderText}
        options={options || []}
        value={multipleValues}
        onChange={handleMultipleChange}
        getOptionLabel={option => {
          return option.label ?? '';
        }}
        renderOption={(optionProps, option: Option) => {
          return (
            <Box
              component="li"
              {...optionProps}
              sx={{
                display: 'flex',
                width: '100%',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                flexDirection: 'row',
                justifyContent: 'space-between !important',
                padding: '8px 10px',
                marginBlock: '1px',
                borderRadius: '4px',
                color: menuItemColor,
                '&.MuiAutocomplete-option[aria-selected="true"]': {
                  background: colors.brand[50],
                  fontWeight: 600,
                },
              }}
              key={option.id}
            >
              <span>{option.label}</span>{' '}
              {option?.customLabel && (
                <Chip
                  isTooltip={false}
                  size="sm"
                  sx={{
                    backgroundColor:
                      option.type === 1 ? theme.palette.bg.primaryHover : theme.palette.bg.active,
                    borderColor: theme.palette.border.primarySubtle,
                  }}
                  label={option?.customLabel}
                />
              )}
            </Box>
          );
        }}
        onBlur={() => {
          if (debounceRef.current) clearTimeout(debounceRef.current);
          onChange && onChange('', multipleValues);
        }}
        multiple
        renderTags={(v, getTagProps) => {
          return (
            typeof v !== 'string' &&
            v?.map((option, index) => {
              const { key, ...tagProps } = getTagProps({ index });
              return (
                <Chip
                  isBackgroundFilled
                  isBordered={false}
                  isTooltip={false}
                  color="brand"
                  label={option?.label}
                  size="lg"
                  key={key}
                  {...tagProps}
                />
              );
            })
          );
        }}
        disabled={disabled}
      />
    );
  }

  if (type === 'dd') {
    return (
      <Select
        labelSeparate
        labelID={`dd-${id}`}
        labelWeight="semiBold"
        fullWidth
        variant="outlined"
        labelColor={colors.grey[400]}
        label={labelText}
        placeholder={placeholderText}
        options={options}
        onChange={(e, v) => {
          if (typeof e.target.value === 'string') {
            setInputValue(e.target.value);
            if (onChange) {
              onChange(e.target.value);
            }
          }
        }}
        value={inputValue}
        error={hasError}
        helperText={helper}
        autoFocus={autoFocus}
        disabled={disabled}
      />
    );
  }

  if (type === 'country') {
    return (
      <CountrySelect
        labelSeparate
        labelWeight="semiBold"
        fullWidth
        variant="outlined"
        labelColor={colors.grey[400]}
        required={required}
        valueType="countryCode"
        id={`form-country-${id}`}
        label={labelText}
        placeholder={placeholderText}
        name="countryCode"
        onChange={handleCountryChange}
        value={inputValue as CountryCode}
        sx={{ '& .MuiInputBase-root ': { height: '44px' } }}
        error={hasError}
        helperText={helper}
        autoFocus={autoFocus}
        disabled={disabled}
        onClose={() => {
          if (!countryInputRef.current && onChange) {
            onChange('');
          }
        }}
      />
    );
  }

  if (type === 'phone') {
    return (
      <PhoneNumberInput
        labelSeparate
        labelWeight="semiBold"
        fullWidth
        variant="outlined"
        labelColor={colors.grey[400]}
        required={required}
        label={labelText}
        placeholder={placeholderText}
        onChange={handlePhoneNumberChange}
        value={phone}
        onBlur={() => {
          if (debounceRef.current) clearTimeout(debounceRef.current);
          onChange && onChange('', phone);
        }}
        error={hasError}
        helperText={helper}
        autoFocus={autoFocus}
        disabled={disabled}
      />
    );
  }

  if (type === 'number') {
    return (
      <NumberInput
        labelSeparate
        labelWeight="semiBold"
        fullWidth
        variant="outlined"
        labelColor={colors.grey[400]}
        required={required}
        label={labelText}
        placeholder={placeholderText}
        value={inputValue}
        onChange={handleTextChange}
        onBlur={handleTextBlur}
        error={hasError}
        helperText={helper}
        autoFocus={autoFocus}
        disabled={disabled}
      />
    );
  }
  if (type === 'date') {
    return (
      <DatePicker
        id={id}
        shouldDisableDate={shouldDisableDate}
        labelSeparate
        labelWeight="semiBold"
        inputVariant="outlined"
        labelColor={colors.grey[400]}
        required={required}
        label={labelText}
        placeholder={placeholderText}
        initialDate={formatDate(inputValue as unknown as Date)}
        error={hasError}
        formatErrorHelperText={t`Use the format DD/MMM/YYYY.`}
        helperText={hasError ? helper : ''}
        disabled={disabled}
        onBlur={date => handleDateBlur(formatDate(date?.toDate()))}
        onChange={(_e, date) => handledateChange(formatDate(date?.toDate()))}
      />
    );
  }
  return null;
};
